import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { File } from "lucide-react";
type ErrorData = {
  type: string;
  iframeId: string;
  message: string;
  source: string;
  lineno: number;
  colno: number;
  stack: string;
};

type Files = {
  [filename: string]: { code: string; hidden?: boolean };
};

type ErrorListProps = {
  errors: ErrorData[];
  files: Files;
};

export function getFileName(url: string) {
  try {
    const pathname = new URL(url).pathname;
    const parts = pathname.split("/");
    let file = parts.pop() || ""; // get last part, or empty if path ends with "/"
    if (!file) file = "index.html";
    return "/" + file;
  } catch {
    // fallback if not a valid URL
    const p = url.split("/");
    let file = p[p.length - 1] || "";
    if (!file) file = "index.html";
    return "/" + file;
  }
}

function getCodeSnippet(
  fileCode: string | undefined,
  errorLine: number | undefined,
  contextLines = 1,
) {
  if (!fileCode || !errorLine) return null;
  const lines = fileCode.split("\n");
  const lineIndex = errorLine - 1;
  const start = Math.max(0, lineIndex - contextLines);
  const end = Math.min(lines.length, lineIndex + contextLines + 1);

  return (
    <pre className="rounded-lg bg-red-200 text-zinc-50 text-xs p-3 my-2">
      {lines.slice(start, end).map((line, i) => {
        const currLineNum = start + i + 1;
        const isError = currLineNum === errorLine;
        return (
          <div
            key={currLineNum}
            className={
              "flex gap-2" +
              (isError
                ? " bg-red-700/60 text-red-100 font-bold rounded"
                : " text-zinc-600")
            }
          >
            <span className="select-none text-zinc-500 pr-2 w-8 text-right">
              {currLineNum}
            </span>
            <span className="whitespace-pre-wrap break-words flex-1">
              {line}
            </span>
          </div>
        );
      })}
    </pre>
  );
}
export const ErrorList: React.FC<ErrorListProps> = ({ errors, files }) => {
  if (!errors.length) return null;

  return (
    <div className="space-y-4">
      {errors.map((err, i) => {
        const fileName = getFileName(err.source);
        const fileCode = files[fileName]?.code;
        return (
          <Card
            key={i}
            className="bg-red-50 border-destructive shadow rounded-lg"
          >
            <CardContent className="px-2 flex flex-col gap-2">
              <div className="font-bold text-base text-destructive">
                {err.message.replace(/^Uncaught\s+/i, "")}
              </div>
              <div className="text-md text-destructive">
                <span className="flex gap-2">
                  <File className="size-4" />
                  <span>
                    {fileName.replace(/^\//, "")} line{" "}
                    {err.lineno != null ? `${err.lineno}` : ""}
                  </span>
                </span>
              </div>
              {fileCode ? (
                getCodeSnippet(fileCode, err.lineno, 1)
              ) : (
                <div className="text-xs italic text-zinc-600 mt-2">
                  Source code not available for this file.
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
